import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import InputForm from '../components/MemberRegistrationInputForm';
import styles from '../assets/css/member-registration.module.css';
import routeStyles from '../assets/css/route-check.module.css';
import { convertAllSpacesInObject, convertAllSpacesInArray } from '../utils/regex';
import { adminService } from '../services/adminService';
import CustomButtons from '../components/CustomButtons';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_ROUTE_ROWS = 10;

function AdminMemberRegistrationDetail() {
  const navigate = useNavigate();

  // --- ユーザー基本情報 ---
  const [basicData, setBasicData] = useState({
    email: '', name: '', furigana: '', branch_id: '', role_id: ''
  });
  const [branches, setBranches] = useState([]);
  const roles = [
    { id: 2, name: '管理者' },
    { id: 3, name: '人事' },
    { id: 4, name: '支社長' },
    { id: 5, name: '一般社員' }
  ];

  // --- メンバー登録情報 ---
  const defaultFormData = {
    name: '', furigana: '', birthdate: '1990-01-01',
    postal_code: '', address_resident: '', postal_code_post: '', address_post: '', nearest_station: '',
    phone: '', blood_type: '',
    emergency_name: '', emergency_furigana: '', emergency_relationship: '', emergency_phone: '',
    bank_name: '', branch_name: '', account_type: '', account_number: '',
    has_dependents: ''
  };
  const [formData, setFormData] = useState(defaultFormData);
  const [familyMembers, setFamilyMembers] = useState([
    { name: '', furigana: '', birthdate: '', relationship: '', cohabitation: '', occupation: '', income_range: '' }
  ]);

  // --- 交通経路 ---
  const [commuteType, setCommuteType] = useState('');
  const [routes, setRoutes] = useState([
    { routeName: '', fromStation: '', toStation: '', oneWayCost: '', commuterPassCost: '' }
  ]);
  const [busRequired, setBusRequired] = useState('');
  const [walkingImpossible, setWalkingImpossible] = useState('');
  const [routeAttachment, setRouteAttachment] = useState(null);

  // --- 写真許可 ---
  const [photoApproval, setPhotoApproval] = useState(null);
  const [photoSignatureImage, setPhotoSignatureImage] = useState(null);

  // --- 機密保持 ---
  const [confidentialitySignatureImage, setConfidentialitySignatureImage] = useState(null);

  // --- UI状態 ---
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const data = await adminService.getBranches();
        setBranches(data.branches || []);
      } catch (e) {
        console.error('支社一覧取得エラー:', e);
      }
    };
    fetchBranches();
  }, []);

  useEffect(() => {
    if (errors.length > 0) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [errors]);

  // 氏名同期: basicData.name → formData.name
  useEffect(() => {
    setFormData(prev => ({ ...prev, name: basicData.name, furigana: basicData.furigana }));
  }, [basicData.name, basicData.furigana]);

  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setBasicData(prev => ({ ...prev, [name]: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFamilyChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...familyMembers];
    updated[index][name] = value;
    setFamilyMembers(updated);
  };

  const addFamilyMember = () => {
    setFamilyMembers([...familyMembers, { name: '', furigana: '', birthdate: '', relationship: '', cohabitation: '', occupation: '', income_range: '' }]);
  };

  const removeFamilyMember = (index) => {
    if (familyMembers.length <= 1) return;
    setFamilyMembers(familyMembers.filter((_, i) => i !== index));
  };

  const handleRouteChange = (index, field, value) => {
    const newRoutes = [...routes];
    newRoutes[index][field] = value;
    setRoutes(newRoutes);
  };

  const addRouteRow = () => {
    if (routes.length >= MAX_ROUTE_ROWS) return;
    setRoutes([...routes, { routeName: '', fromStation: '', toStation: '', oneWayCost: '', commuterPassCost: '' }]);
  };

  const removeRouteRow = (index) => {
    if (routes.length === 1) return;
    setRoutes(routes.filter((_, i) => i !== index));
  };

  const validateFileSize = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      setErrors(prev => [...prev, `ファイル「${file.name}」が10MBを超えています。`]);
      return false;
    }
    return true;
  };

  const handleRouteFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!validateFileSize(file)) { e.target.value = ''; return; }
    const reader = new FileReader();
    reader.onloadend = () => setRouteAttachment(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSignatureFileChange = (setter) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!validateFileSize(file)) { e.target.value = ''; return; }
    const reader = new FileReader();
    reader.onloadend = () => setter(reader.result);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const newErrors = [];

    // 基本情報
    if (!basicData.email) newErrors.push("メールアドレスは必須です。");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(basicData.email)) newErrors.push("有効なメールアドレスを入力してください。");
    if (!basicData.name) newErrors.push("氏名は必須です。");
    if (!basicData.furigana) newErrors.push("フリガナは必須です。");
    else if (!/^[ァ-ヶー 　]+$/.test(basicData.furigana)) newErrors.push("フリガナは全角カタカナで入力してください。");
    if (!basicData.branch_id) newErrors.push("支社は必須です。");
    if (!basicData.role_id) newErrors.push("権限は必須です。");

    // メンバー登録情報
    if (!formData.birthdate) newErrors.push("生年月日は必須です。");
    else if (new Date(formData.birthdate) >= new Date()) newErrors.push("生年月日は今日より前の日付を入力してください。");
    if (!formData.postal_code) newErrors.push("郵便番号（住民票）は必須です。");
    else if (!/^\d{3}-?\d{4}$/.test(formData.postal_code)) newErrors.push("郵便番号（住民票）は7桁で入力してください（例: 107-0052）。");
    if (!formData.address_resident) newErrors.push("住所（住民票のある）は必須です。");
    if (formData.postal_code_post && !/^\d{3}-?\d{4}$/.test(formData.postal_code_post)) newErrors.push("郵便番号（郵送先）は7桁で入力してください（例: 107-0052）。");
    if (!formData.nearest_station) newErrors.push("最寄駅は必須です。");
    if (!formData.phone) newErrors.push("電話番号は必須です。");
    else if (!/^0\d{9,10}$|^0\d{1,3}-\d{1,4}-\d{3,4}$/.test(formData.phone)) newErrors.push("電話番号の形式が正しくありません。");
    if (!formData.blood_type) newErrors.push("血液型は必須です。");
    if (!formData.bank_name) newErrors.push("銀行名は必須です。");
    if (!formData.branch_name) newErrors.push("支店名は必須です。");
    if (!formData.account_type) newErrors.push("口座種別は必須です。");
    if (!formData.account_number) newErrors.push("口座番号は必須です。");
    else if (!/^\d{7}$/.test(formData.account_number)) newErrors.push("口座番号は半角数字7桁で入力してください。");
    if (!formData.emergency_name) newErrors.push("緊急連絡先の氏名は必須です。");
    if (!formData.emergency_furigana) newErrors.push("緊急連絡先のフリガナは必須です。");
    if (!formData.emergency_relationship) newErrors.push("緊急連絡先の続柄は必須です。");
    if (!formData.emergency_phone) newErrors.push("緊急連絡先の電話番号は必須です。");

    if (!formData.has_dependents) {
      newErrors.push("扶養家族の有無は必須です。");
    } else if (formData.has_dependents === '有') {
      const hasFilledMember = familyMembers.some(member => Object.values(member).some(value => value));
      if (!hasFilledMember) {
        newErrors.push("扶養家族「有」の場合は、扶養家族を1名以上入力してください。");
      }
      familyMembers.forEach((member, index) => {
        const anyInput = Object.values(member).some(value => value);
        if (anyInput) {
          if (!member.name) newErrors.push(`扶養家族 ${index + 1} の氏名は必須です。`);
          if (!member.furigana) newErrors.push(`扶養家族 ${index + 1} のフリガナは必須です。`);
          if (!member.birthdate) newErrors.push(`扶養家族 ${index + 1} の生年月日は必須です。`);
          if (!member.relationship) newErrors.push(`扶養家族 ${index + 1} の続柄は必須です。`);
          if (!member.cohabitation) newErrors.push(`扶養家族 ${index + 1} の同居欄は必須です。`);
          if (!member.occupation) newErrors.push(`扶養家族 ${index + 1} の職業は必須です。`);
          if (member.relationship === '配偶者' && !member.income_range) {
            newErrors.push(`扶養家族 ${index + 1}（配偶者）の年収区分は必須です。`);
          }
        }
      });
    }

    // 交通経路
    if (!commuteType) newErrors.push("通勤手段を選択してください。");
    if (commuteType === 'transport') {
      if (!busRequired) newErrors.push("バス利用の有無を選択してください。");
      if (!walkingImpossible) newErrors.push("徒歩可否を選択してください。");
      if (!routeAttachment) newErrors.push("経路の添付ファイルが必要です。");
      routes.forEach((r, i) => {
        if (!r.routeName.trim()) newErrors.push(`交通経路 ${i + 1} の交通機関名は必須です。`);
        if (!r.fromStation.trim()) newErrors.push(`交通経路 ${i + 1} の発駅は必須です。`);
        if (!r.toStation.trim()) newErrors.push(`交通経路 ${i + 1} の着駅は必須です。`);
        if (!r.oneWayCost) newErrors.push(`交通経路 ${i + 1} の片道料金は必須です。`);
        if (!r.commuterPassCost) newErrors.push(`交通経路 ${i + 1} の定期代は必須です。`);
      });
    }

    // 写真許可
    if (photoApproval === null) newErrors.push("写真使用許可の承諾/非承諾を選択してください。");

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const resetForm = () => {
    setBasicData({ email: '', name: '', furigana: '', branch_id: '', role_id: '' });
    setFormData(defaultFormData);
    setFamilyMembers([{ name: '', furigana: '', birthdate: '', relationship: '', cohabitation: '', occupation: '', income_range: '' }]);
    setCommuteType('');
    setRoutes([{ routeName: '', fromStation: '', toStation: '', oneWayCost: '', commuterPassCost: '' }]);
    setBusRequired('');
    setWalkingImpossible('');
    setRouteAttachment(null);
    setPhotoApproval(null);
    setPhotoSignatureImage(null);
    setConfidentialitySignatureImage(null);
    setErrors([]);
    setSuccessMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!validate()) return;

    setLoading(true);
    setSuccessMessage('');

    try {
      const sanitizedFormData = convertAllSpacesInObject(formData);
      // 「無」の場合は入力途中の扶養家族データを送らない
      const sanitizedFamilyMembers = formData.has_dependents === '無'
        ? []
        : convertAllSpacesInArray(familyMembers);

      const hasTransport = commuteType === 'transport';

      const data = {
        email: basicData.email,
        name: basicData.name,
        furigana: basicData.furigana,
        branch_id: basicData.branch_id,
        role_id: basicData.role_id,
        formData: sanitizedFormData,
        familyMembers: sanitizedFamilyMembers,
        routeData: hasTransport ? {
          busRequired: busRequired === 'はい',
          walkingImpossible: walkingImpossible === 'はい',
          attachment: routeAttachment,
          routes: routes
        } : null,
        photoPermission: {
          isApproved: photoApproval,
          signatureData: photoSignatureImage
        },
        confidentiality: {
          signatureData: confidentialitySignatureImage
        }
      };

      const result = await adminService.saveExistingMember(data);
      if (result.emailFailed) {
        setSuccessMessage('登録完了しました。ただしメール送信に失敗しました。手動でログイン情報をお伝えください。');
      } else {
        setSuccessMessage('登録完了しました。ログイン情報をメールで送信しました。');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setErrors([error.message || '登録に失敗しました。']);
    } finally {
      setLoading(false);
    }
  };

  const hasTransport = commuteType === 'transport';

  return (
    <div className={styles.mainContents}>
      <div className={styles.container}>
        <div className={styles["registration-card"]}>
          <h1 className={`${styles["text-primary"]} text-center mb-4`}>既存メンバー登録</h1>

          {errors.length > 0 && (
            <div className="alert alert-danger">
              <ul className="mb-0">
                {errors.map((error, i) => <li key={i}>{error}</li>)}
              </ul>
            </div>
          )}

          {successMessage && (
            <div className="alert alert-success">
              {successMessage}
              <div className="mt-2" style={{display:'flex',gap:8}}>
                <button className="btn btn-primary btn-sm" onClick={() => navigate('/member-list')}>メンバー一覧へ</button>
                <button className="btn btn-outline-primary btn-sm" onClick={resetForm}>続けて別のメンバーを登録</button>
              </div>
            </div>
          )}

          {!successMessage && (
            <form onSubmit={handleSubmit}>
              {/* === セクション1: アカウント情報 === */}
              <section className="mb-5">
                <h2 className={styles["section-title"]}>アカウント情報</h2>
                <div className="mb-3">
                  <label className="form-label">氏名 <span className="text-danger">（必須）</span></label>
                  <input type="text" name="name" className="form-control" value={basicData.name} onChange={handleBasicChange} placeholder="例: 山田　太郎" autoComplete="off" />
                </div>
                <div className="mb-3">
                  <label className="form-label">フリガナ <span className="text-danger">（必須）</span></label>
                  <input type="text" name="furigana" className="form-control" value={basicData.furigana} onChange={handleBasicChange} placeholder="例: ヤマダ　タロウ" autoComplete="off" />
                </div>
                <div className="mb-3">
                  <label className="form-label">メールアドレス <span className="text-danger">（必須）</span></label>
                  <input type="email" name="email" className="form-control" value={basicData.email} onChange={handleBasicChange} placeholder="例: yamada@example.com" autoComplete="off" />
                </div>
                <div className="mb-3">
                  <label className="form-label">支社 <span className="text-danger">（必須）</span></label>
                  <select name="branch_id" className="form-control" value={basicData.branch_id} onChange={handleBasicChange}>
                    <option value="">選択してください</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">権限 <span className="text-danger">（必須）</span></label>
                  <select name="role_id" className="form-control" value={basicData.role_id} onChange={handleBasicChange}>
                    <option value="">選択してください</option>
                    {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                <div className="alert alert-info" style={{fontSize:'0.9rem'}}>
                  パスワードは自動生成され、登録メールに記載されます。
                </div>
              </section>

              {/* === セクション2: メンバー登録情報 === */}
              <InputForm
                formData={formData}
                familyMembers={familyMembers}
                errors={[]}
                onInputChange={handleInputChange}
                onFamilyChange={handleFamilyChange}
                onAddFamilyMember={addFamilyMember}
                onRemoveFamilyMember={removeFamilyMember}
                readOnlyFields={['name', 'furigana']}
              />

              {/* === セクション3: 交通経路 === */}
              <section className="mb-5">
                <h2 className={styles["section-title"]}>交通経路情報</h2>

                <div className="mb-3">
                  <label className="form-label">通勤手段 <span className="text-danger">（必須）</span></label>
                  <div className={routeStyles?.["radio-group"] || ''}>
                    <div className="form-check">
                      <input type="radio" name="commuteType" id="commute-transport" value="transport" checked={commuteType === 'transport'} onChange={(e) => setCommuteType(e.target.value)} />
                      <label htmlFor="commute-transport" style={{cursor:'pointer',padding:'4px 8px'}}>交通機関を利用する</label>
                    </div>
                    <div className="form-check">
                      <input type="radio" name="commuteType" id="commute-walk" value="walk" checked={commuteType === 'walk'} onChange={(e) => setCommuteType(e.target.value)} />
                      <label htmlFor="commute-walk" style={{cursor:'pointer',padding:'4px 8px'}}>徒歩のみ</label>
                    </div>
                    <div className="form-check">
                      <input type="radio" name="commuteType" id="commute-remote" value="remote" checked={commuteType === 'remote'} onChange={(e) => setCommuteType(e.target.value)} />
                      <label htmlFor="commute-remote" style={{cursor:'pointer',padding:'4px 8px'}}>フルリモート（通勤なし）</label>
                    </div>
                  </div>
                </div>

                {hasTransport && (
                  <>
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr><th>交通機関名</th><th>発駅</th><th>着駅</th><th>片道料金（円）</th><th>定期代（円）</th><th>操作</th></tr>
                        </thead>
                        <tbody>
                          {routes.map((route, index) => (
                            <tr key={index}>
                              <td><input type="text" className="form-control" placeholder="例: JR山手線" value={route.routeName} onChange={(e) => handleRouteChange(index, 'routeName', e.target.value)} /></td>
                              <td><input type="text" className="form-control" placeholder="例: 新宿" value={route.fromStation} onChange={(e) => handleRouteChange(index, 'fromStation', e.target.value)} /></td>
                              <td><input type="text" className="form-control" placeholder="例: 東京" value={route.toStation} onChange={(e) => handleRouteChange(index, 'toStation', e.target.value)} /></td>
                              <td><input type="number" className="form-control" placeholder="250" value={route.oneWayCost} onChange={(e) => handleRouteChange(index, 'oneWayCost', e.target.value)} /></td>
                              <td><input type="number" className="form-control" placeholder="10000" value={route.commuterPassCost} onChange={(e) => handleRouteChange(index, 'commuterPassCost', e.target.value)} /></td>
                              <td>{routes.length > 1 && <button type="button" className="btn btn-danger btn-sm" onClick={() => removeRouteRow(index)}>削除</button>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {routes.length < MAX_ROUTE_ROWS && (
                      <button type="button" className="btn btn-secondary btn-sm mb-3" onClick={addRouteRow}>行を追加（最大{MAX_ROUTE_ROWS}行）</button>
                    )}

                    <div className="mb-3">
                      <label className="form-label">バス利用 <span className="text-danger">（必須）</span></label>
                      <div>
                        <label style={{cursor:'pointer',marginRight:16}}><input type="radio" name="bus" value="はい" checked={busRequired === 'はい'} onChange={(e) => setBusRequired(e.target.value)} /> はい</label>
                        <label style={{cursor:'pointer'}}><input type="radio" name="bus" value="いいえ" checked={busRequired === 'いいえ'} onChange={(e) => setBusRequired(e.target.value)} /> いいえ</label>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">徒歩不可（直線距離2km以上） <span className="text-danger">（必須）</span></label>
                      <div>
                        <label style={{cursor:'pointer',marginRight:16}}><input type="radio" name="walk" value="はい" checked={walkingImpossible === 'はい'} onChange={(e) => setWalkingImpossible(e.target.value)} /> はい</label>
                        <label style={{cursor:'pointer'}}><input type="radio" name="walk" value="いいえ" checked={walkingImpossible === 'いいえ'} onChange={(e) => setWalkingImpossible(e.target.value)} /> いいえ</label>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">添付ファイル（地図や経路） <span className="text-danger">（必須）</span></label>
                      <small className="text-muted d-block mb-1">JPEG/PNG/GIF/WebP形式、10MB以下</small>
                      <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleRouteFileChange} className="form-control" />
                    </div>
                  </>
                )}

                {commuteType && !hasTransport && (
                  <div className="alert alert-info" style={{fontSize:'0.9rem'}}>
                    {commuteType === 'walk' ? '徒歩通勤のため、交通経路の入力は不要です。' : 'フルリモートのため、交通経路の入力は不要です。'}
                  </div>
                )}
              </section>

              {/* === セクション4: 写真使用許可 === */}
              <section className="mb-5">
                <h2 className={styles["section-title"]}>写真使用許可</h2>
                <div className="mb-3">
                  <label className="form-label">画像使用承諾 <span className="text-danger">（必須）</span></label>
                  <div>
                    <label style={{cursor:'pointer',marginRight:16}}>
                      <input type="radio" name="photoApproval" value="yes" checked={photoApproval === true} onChange={() => setPhotoApproval(true)} /> 承諾する
                    </label>
                    <label style={{cursor:'pointer'}}>
                      <input type="radio" name="photoApproval" value="no" checked={photoApproval === false} onChange={() => setPhotoApproval(false)} /> 承諾しない
                    </label>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">署名画像（任意）</label>
                  <small className="text-muted d-block mb-1">本人が紙に署名した画像をスキャンまたは撮影してアップロードしてください（JPEG/PNG/GIF/WebP、10MB以下）</small>
                  <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleSignatureFileChange(setPhotoSignatureImage)} className="form-control" />
                  {photoSignatureImage && <img src={photoSignatureImage} alt="写真許可署名プレビュー" style={{maxWidth:'100%',maxHeight:150,marginTop:8,border:'1px solid #dee2e6',borderRadius:4}} />}
                </div>
              </section>

              {/* === セクション5: 機密保持誓約 === */}
              <section className="mb-5">
                <h2 className={styles["section-title"]}>機密保持誓約</h2>
                <div className="mb-3">
                  <label className="form-label">署名画像（任意）</label>
                  <small className="text-muted d-block mb-1">本人が紙に署名した画像をスキャンまたは撮影してアップロードしてください（JPEG/PNG/GIF/WebP、10MB以下）</small>
                  <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleSignatureFileChange(setConfidentialitySignatureImage)} className="form-control" />
                  {confidentialitySignatureImage && <img src={confidentialitySignatureImage} alt="機密保持署名プレビュー" style={{maxWidth:'100%',maxHeight:150,marginTop:8,border:'1px solid #dee2e6',borderRadius:4}} />}
                </div>
              </section>

              {/* === 送信 === */}
              <div className="d-grid mt-4">
                <CustomButtons
                  leftLabel="戻る"
                  rightLabel="登録する"
                  rightLoadingLabel="登録中..."
                  rightType="submit"
                  onLeftClick={() => navigate(-1)}
                  rightDisabled={loading}
                  rightLoading={loading}
                />
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminMemberRegistrationDetail;
